import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import './supabase-debugger.scss';

interface TableInfo {
  name: string;
  exists: boolean;
  rowCount?: number;
  error?: string;
}

const SupabaseDebugger: React.FC = () => {
  const { user } = useAuth();
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  // const [envVars, setEnvVars] = useState<{url: string, key: string}>({
  //   url: process.env.REACT_APP_SUPABASE_URL || 'Not set',
  //   key: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Set (hidden)' : 'Not set'
  // });
  const [testLogResult, setTestLogResult] = useState<{success: boolean, message: string} | null>(null);

  // Check connection to Supabase
  useEffect(() => {
    const checkConnection = async () => {
      try {
        // Simple query to check connection
        const { data, error } = await supabase
          .from('conversation_logs')
          .select('*', { count: 'exact' })
          .limit(0);
        
        if (error) {
          if (error.code === '42P01') {
            // Table doesn't exist, but connection works
            setConnectionStatus('connected');
            setConnectionError(null);
            setTables(prev => [...prev, { 
              name: 'conversation_logs', 
              exists: false,
              error: 'Table does not exist. Please run the SQL setup script.'
            }]);
          } else {
            setConnectionStatus('error');
            setConnectionError(`Connection error: ${error.message} (${error.code})`);
          }
        } else {
          setConnectionStatus('connected');
          setConnectionError(null);
          setTables(prev => [...prev, { 
            name: 'conversation_logs', 
            exists: true,
            rowCount: data.length
          }]);
        }
      } catch (err) {
        setConnectionStatus('error');
        setConnectionError(`Unexpected error: ${err instanceof Error ? err.message : String(err)}`);
      }
    };

    checkConnection();
  }, []);

  // Test logging a message
  const testLogMessage = async () => {
    try {
      setTestLogResult({ success: false, message: 'Testing...' });
      
      const testMessage = {
        user_id: user?.id || null,
        timestamp: new Date().toISOString(),
        message_type: 'test',
        role: 'user' as const,
        content: 'This is a test message from the debugger',
        session_id: 'test-session-' + new Date().toISOString(),
        metadata: { source: 'debugger' }
      };
      
      const { data, error } = await supabase
        .from('conversation_logs')
        .insert(testMessage)
        .select();
      
      if (error) {
        setTestLogResult({ 
          success: false, 
          message: `Error logging test message: ${error.message} (${error.code})`
        });
      } else {
        setTestLogResult({ 
          success: true, 
          message: `Successfully logged test message with ID: ${data?.[0]?.id || 'unknown'}`
        });
        
        // Refresh the table count
        const { data: countData } = await supabase
          .from('conversation_logs')
          .select('*')
          .limit(1000);
          
        setTables(prev => prev.map(table => 
          table.name === 'conversation_logs' 
            ? { ...table, rowCount: countData?.length || 0 } 
            : table
        ));
      }
    } catch (err) {
      setTestLogResult({ 
        success: false, 
        message: `Unexpected error: ${err instanceof Error ? err.message : String(err)}`
      });
    }
  };

  return (
    <div className="supabase-debugger">
      <h2>Supabase Connection Debugger</h2>
      
      <section className="debug-section">
        <h3>Connection Status</h3>
        <div className={`status-indicator ${connectionStatus}`}>
          {connectionStatus === 'checking' && 'Checking connection...'}
          {connectionStatus === 'connected' && 'Connected to Supabase'}
          {connectionStatus === 'error' && 'Connection Error'}
        </div>
        {connectionError && <div className="error-message">{connectionError}</div>}
      </section>
      
      <section className="debug-section">
        <h3>Environment Variables</h3>
        <div className="env-vars">
          <div className="env-var">
            <span className="env-var-name">REACT_APP_SUPABASE_URL:</span>
            {/* <span className={`env-var-value ${envVars.url === 'Not set' ? 'error' : ''}`}>
              {envVars.url}
            </span> */}
          </div>
          <div className="env-var">
            <span className="env-var-name">REACT_APP_SUPABASE_ANON_KEY:</span>
            {/* <span className={`env-var-value ${envVars.key === 'Not set' ? 'error' : ''}`}>
              {envVars.key}
            </span> */}
          </div>
        </div>
      </section>
      
      <section className="debug-section">
        <h3>Required Tables</h3>
        <div className="tables-list">
          {tables.map((table, index) => (
            <div key={index} className={`table-item ${table.exists ? 'exists' : 'missing'}`}>
              <span className="table-name">{table.name}</span>
              <span className="table-status">
                {table.exists 
                  ? `Exists (${table.rowCount} rows)` 
                  : 'Missing'}
              </span>
              {table.error && <div className="table-error">{table.error}</div>}
            </div>
          ))}
        </div>
      </section>
      
      <section className="debug-section">
        <h3>Test Logging</h3>
        <button 
          className="test-button" 
          onClick={testLogMessage}
          disabled={connectionStatus !== 'connected' || !tables.some(t => t.name === 'conversation_logs' && t.exists)}
        >
          Log Test Message
        </button>
        
        {testLogResult && (
          <div className={`test-result ${testLogResult.success ? 'success' : 'error'}`}>
            {testLogResult.message}
          </div>
        )}
      </section>
      
      <section className="debug-section">
        <h3>Troubleshooting</h3>
        <div className="troubleshooting-tips">
          <p><strong>If connection fails:</strong></p>
          <ol>
            <li>Check that your Supabase URL and anon key are correctly set in the .env file</li>
            <li>Verify that your Supabase project is running and accessible</li>
            <li>Check for network issues or CORS restrictions</li>
          </ol>
          
          <p><strong>If table is missing:</strong></p>
          <ol>
            <li>Run the SQL setup script in the Supabase SQL editor</li>
            <li>Check for SQL errors in the console</li>
          </ol>
          
          <p><strong>SQL Setup Script:</strong></p>
          <pre className="sql-script">
{`-- Create conversation_logs table
CREATE TABLE public.conversation_logs (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    user_id UUID REFERENCES auth.users(id),
    timestamp TIMESTAMPTZ NOT NULL DEFAULT NOW(),
    message_type TEXT NOT NULL,
    role TEXT NOT NULL CHECK (role IN ('user', 'bot')),
    content TEXT NOT NULL,
    session_id TEXT NOT NULL,
    metadata JSONB,
    created_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
);

-- Add RLS policies
ALTER TABLE public.conversation_logs ENABLE ROW LEVEL SECURITY;

-- Create policy to allow users to read only their own logs
CREATE POLICY "Users can view their own logs" 
    ON public.conversation_logs 
    FOR SELECT 
    USING (auth.uid() = user_id OR user_id IS NULL);

-- Create policy to allow authenticated users to insert logs
CREATE POLICY "Authenticated users can insert logs" 
    ON public.conversation_logs 
    FOR INSERT 
    WITH CHECK (auth.role() = 'authenticated');

-- Create policy to allow anon users to insert logs
CREATE POLICY "Anonymous users can insert logs" 
    ON public.conversation_logs 
    FOR INSERT 
    WITH CHECK (user_id IS NULL);

-- Create indexes
CREATE INDEX idx_conversation_logs_user_id ON public.conversation_logs(user_id);
CREATE INDEX idx_conversation_logs_session_id ON public.conversation_logs(session_id);
CREATE INDEX idx_conversation_logs_timestamp ON public.conversation_logs(timestamp);

-- Grant access
GRANT SELECT, INSERT ON public.conversation_logs TO authenticated, anon;`}
          </pre>
        </div>
      </section>
    </div>
  );
};

export default SupabaseDebugger;
